<template lang="pug">
div
  .quick-actions(:class="{collapsed: quickActionsHaveFaded}")
    transition(name="fade" @after-leave="endQuickActionsFade")
      .quick-actions-content(v-show="showQuickActions")
        button.collapse-icon-button(type="button" @click="showQuickActions = false")
          BriqIcon(name="icon-arrow-leftright-icon")
        .quick-actions-title
          .actions-icon
            BriqIcon(name="icon-manage-menu-icon"  :customStyle="quickActionsIconCustomStyle")
          h4.mb-XXS.grey STATUS
        BriqButton(
          v-if="isBookingOriginOnline" 
          :variant="isMarkedAsCheckedUser ? `secondary` : `dark-2`" 
          @click="clickMarkAsChecked" 
          :disabled="disableMarkAsCheckedActions"
          data-test-id="mark-checked"
          ) 
            .flex
              div {{ markAsCheckedLabel }}
              div.marked-as-checked-reset-icon(v-if="isMarkedAsCheckedUser")
                BriqIcon(name="icon-reset")
        BriqButton(variant="dark-2" v-if="businessAllowsCheckin" @click="clickCheckIn" :disabled="disableAttendanceActions" data-test-id="check-in") {{ checkInLabel }}
        BriqButton(variant="dark-2" v-if="businessAllowsNoShow" @click="clickNoShow" :disabled="disableNoShow" data-test-id="mark-as-no-show") {{ noShowLabel }}
        BriqButton(variant="dark-2" @click="showBookingStatusDialog = true" :disabled="disableChangeStatus" data-test-id="change-status") Change status
        .quick-actions-title.mt-S.mb-XXS
          .actions-icon
            BriqIcon(name="icon-quick-menu-icon"  :customStyle="quickActionsIconCustomStyle")
          h4.grey ACTIONS
        BriqButton(variant="dark-2" v-if="businessAllowsShowOnPlanboard" @click="showOnPlanboard" :disabled="isPending" data-test-id="show-on-planboard") Show on planboard
        BriqButton(variant="dark-2" @click="showEmailDialog = true" :disabled="disableSendMail" data-test-id="send-mail") Send mail
        .quick-actions-title.mt-S.mb-XXS(v-if="businessAllowsAnyPaymentAction")
          .actions-icon
            BriqIcon(name="icon-euro-icon"  :customStyle="quickActionsIconCustomStyle")
          h4.grey PAYMENTS
        BriqButton(
          variant="dark-2"
          v-if="businessAllowsAdminPayments"
          @click="showAddPaymentDialog = true"
          :disabled="disablePayments"
          data-test-id="quick-action-add-payment-button"
          ) Add payment
        BriqButton(
          variant="dark-2"
          v-if="businessAllowsCashRefund"
          @click="openRefundDialog"
          :disabled="disableRefund"
          data-test-id="cash-refund"
          ) Cash refund
        BriqButton(
          variant="dark-2"
          v-if="businessAllowsInvoice"
          @click="showInvoiceDialog = true"
          :disabled="disableInvoice"
          data-test-id="mark-as-paid-by-invoice"
          ) Mark as paid by invoice
        template(v-for="(integration, i) in integrationsWithActions")
          .quick-actions-title.mt-S.mb-XXS(v-if="integration")
            .actions-icon
              BriqIcon(name="icon-search-icon"  :customStyle="quickActionsIconCustomStyle")
            h4.grey {{integration.header}}
          template(v-for="(action, i) in integration.actions")
            BriqButton(
              variant="dark-2"
              @click="peformIntegrationAction(integration, action.action)"
              ) {{action.actionText}}


  SendEmailDialog(
    :showEmailDialog="showEmailDialog"
    @close="showEmailDialog = false"
    @sendEmail="sendEmailProxy"
    )

  AddPaymentDialog(
    :disabled="isPending"
    :show="showAddPaymentDialog"
    :outstandingAmount="outstandingAmount"
    @save="addPayment"
    @close="showAddPaymentDialog = false"
    )

  CashRefundDialog(
    :disabled="isPending"
    :show="showRefundDialog"
    :maxRefundable="maxRefundable"
    @save="addRefund"
    @close="showRefundDialog = false"
    )

  BriqDialog(v-show="showBookingStatusDialog" @close="showBookingStatusDialog = false")
    h2.mb-S Change status
    BriqMultiselect.mb-S(:options="bookingStatusOptions" v-model="selectedBookingStatus" data-test-id="change-status-dropdown")
    .flex.justify-center
      BriqButton.mr-XXS(variant="text" @click="showBookingStatusDialog = false" :disabled="isPending" data-test-id="cancel-change-status-button") Cancel
      BriqButton.ml-XXS(@click="saveNewBookingStatus" :disabled="isPending" data-test-id="save-change-status-button") Save

  BriqDialog(v-show="showInvoiceDialog" @close="showInvoiceDialog = false")
    h2.mb-S Mark as invoice
    p.mb-S Are you sure you want to mark this booking as pay by invoice?
    .flex.justify-center
      BriqButton.mr-XXS(variant="text" @click="showInvoiceDialog = false" :disabled="isPending") Cancel
      BriqButton.ml-XXS(@click="saveMarkAsInvoice" :disabled="isPending") Yes

  BriqDialog(v-show="showUpdateCheckInDialog" @close="showUpdateCheckInDialog = false")
    h2.mb-S Update check-in
    p.mb-S Are you sure you want to update check-in?
    .flex.justify-center
      BriqButton.mr-XXS(variant="text" @click="showUpdateCheckInDialog = false" :disabled="isPending" data-test-id="cancel-update-check-in-button") Cancel
      BriqButton.ml-XXS(@click="sendCheckIn({forceRedo: true})" :disabled="isPending" data-test-id="dialog-update-check-in-button") Update check-in
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { AttendanceStatus } from '@v/bookings/details/BookingDetailsApiModel.js'
import AddPaymentDialog from './dialogs/AddPaymentDialog.vue'
import SendEmailDialog from './dialogs/SendEmailDialog.vue'
import CashRefundDialog from './dialogs/CashRefundDialog.vue'
import Permission from '@v/models/UserPermissionEnum.js'
import BusinessFeature from '@v/models/BusinessFeatureEnum.js'
import BriqPriceInput from '@v/components/BriqPriceInput.vue'
import { isEmpty } from 'ramda'

// Please note that cash refund is different from refund payment

export default {
  name: 'QuickActions',
  components: { AddPaymentDialog, BriqPriceInput, CashRefundDialog, SendEmailDialog },
  data() {
    return {
      showQuickActions: true,
      quickActionsHaveFaded: false,
      showEmailDialog: false,
      showRefundDialog: false,
      showAddPaymentDialog: false,
      showBookingStatusDialog: false,
      bookingStatusOptions: ['Active', 'Test', 'Cancelled', 'Concept'], // [Error, Concept, Pending, Expired, Active, Test, Cancelled, Rejected]
      selectedBookingStatus: null,
      showInvoiceDialog: false,
      showUpdateCheckInDialog: false,
      quickActionsIconCustomStyle: { color: '#798696' },
    }
  },
  computed: {
    ...mapState('bookingDetails', ['saveError']),
    ...mapGetters('bookingDetails', [
      'isPending',
      'bookingKey',
      'booking',
      'mainBooker',
      'bookingIsMarkedAsInvoice',
      'maxRefundable',
      'outstandingAmount',
      'externalPaymentMethods',
      'integrationsInfo',
      'integrationFieldDefinitionsList',
    ]),
    ...mapGetters('briqState', ['businessUnitHasFeature', 'userHasPermission']),
    integrationsWithActions() {
      return (
        this.integrationsInfo
          ?.filter(x => x.Actions?.length > 0)
          ?.map(integration => {
            return {
              integrationCode: integration.IdentificationCode,
              header: integration.LocalizedName,
              actions: integration.Actions.map(action => {
                return {
                  actionText: action.ActionName,
                  action: action,
                }
              }),
            }
          }) || []
      )
    },
    isCheckedIn() {
      return this.booking?.AttendanceStatus === AttendanceStatus.CheckedIn
    },
    isNoShow() {
      return this.booking?.AttendanceStatus === AttendanceStatus.NoShow
    },
    isBookingOriginOnline() {
      return this.booking?.Origin === 'Online'
    },
    disableMarkAsCheckedActions() {
      return this.isPending || !this.userHasPermission(Permission.API_Admin_Booking_MarkAsChecked)
    },
    isMarkedAsCheckedUser() {
      return this.booking?.OnlineBookingCheckedUser ? true : false
    },
    markAsCheckedLabel() {
      return this.isMarkedAsCheckedUser ? 'Mark as unchecked' : 'Mark as checked'
    },
    checkInLabel() {
      return this.isCheckedIn ? 'Update check-in' : 'Check in'
    },
    noShowLabel() {
      return this.isNoShow ? 'Undo no show' : 'Mark as no show'
    },
    customerEmail() {
      return this.mainBooker?.Email
    },

    // BU features
    businessAllowsCheckin() {
      return this.businessUnitHasFeature(BusinessFeature.BookingCheckin)
    },
    businessAllowsNoShow() {
      return this.businessUnitHasFeature(BusinessFeature.BookingNoShow)
    },
    businessAllowsShowOnPlanboard() {
      return this.businessUnitHasFeature(BusinessFeature.BookingShowOnPlanboard)
    },
    businessAllowsCashRefund() {
      return this.businessUnitHasFeature(BusinessFeature.BookingCashRefund)
    },
    businessAllowsAdminPayments() {
      return this.businessUnitHasFeature(BusinessFeature.BookingAddPayment)
    },
    businessAllowsInvoice() {
      return this.businessUnitHasFeature(BusinessFeature.BookingPushInvoice)
    },
    businessAllowsAnyPaymentAction() {
      return (
        this.businessAllowsCashRefund ||
        this.businessAllowsAdminPayments ||
        this.businessAllowsInvoice
      )
    },

    bookingStatusIsTest() {
      return this.booking.CurrentStatusCode === 'Test'
    },
    toggleTestLabel() {
      return this.bookingStatusIsTest ? 'Unmark as test' : 'Mark as test'
    },
    disableSendMail() {
      return this.isPending || !this.userHasPermission(Permission.API_Admin_Booking_Mail_Send)
    },
    disablePayments() {
      return (
        this.isPending ||
        !this.userHasPermission(Permission.API_Admin_Booking_Payment_Create) ||
        isEmpty(this.externalPaymentMethods)
      )
    },
    disableRefund() {
      return this.disablePayments || this.maxRefundable === 0
    },
    disableAttendanceActions() {
      return (
        this.isPending ||
        !this.userHasPermission(Permission.API_Admin_Booking_Checkin) ||
        this.booking.CurrentStatusCode === 'Cancelled'
      )
    },
    disableNoShow() {
      return this.disableAttendanceActions || this.isCheckedIn
    },
    disableChangeStatus() {
      return this.isPending || !this.userHasPermission(Permission.API_Admin_Booking_ChangeStatus)
    },
    disableInvoice() {
      return (
        this.isPending ||
        this.bookingIsMarkedAsInvoice ||
        !this.userHasPermission(Permission.API_Admin_Booking_SetNextBillingMethod)
      )
    },
  },
  methods: {
    ...mapActions('bookingDetails', [
      'saveBookingStatus',
      'saveAttendanceStatus',
      'changeMarkAsCheckedStatus',
      'savePayment',
      'sendEmail',
      'markAsInvoice',
    ]),
    endQuickActionsFade() {
      // collapse quick actions panel after content has faded
      this.quickActionsHaveFaded = true
    },
    openRefundDialog() {
      this.showRefundDialog = true
    },
    async clickCheckIn() {
      if (!this.isCheckedIn) {
        await this.sendCheckIn({ forceRedo: false })
      } else {
        this.showUpdateCheckInDialog = true
      }
    },
    async sendCheckIn({ forceRedo }) {
      await this.saveSomething({
        call: () => this.saveAttendanceStatus({ status: AttendanceStatus.CheckedIn, forceRedo }),
        successMessage: 'Booking checked in!',
        errorMessage: 'Something went wrong while checking in',
      }).finally(() => {
        this.showUpdateCheckInDialog = false
      })
    },
    async clickMarkAsChecked() {
      const status = this.isMarkedAsCheckedUser ? 'unchecked' : 'checked'
      await this.saveSomething({
        call: () => this.changeMarkAsCheckedStatus({ checkedIn: !this.isMarkedAsCheckedUser }),
        successMessage: `Booking marked as ${status} succesfully!`,
        errorMessage: 'Something went wrong while checking in',
      })
    },
    async clickNoShow() {
      if (this.isCheckedIn) {
        this.$notifyWarning('Cannot undo check in!')
        return
      }
      const newStatus = this.isNoShow ? AttendanceStatus.Open : AttendanceStatus.NoShow
      await this.saveSomething({
        call: () => this.saveAttendanceStatus({ status: newStatus }),
        successMessage: 'Attendance status saved',
        errorMessage: 'Something went wrong while saving attendance status',
      })
    },
    showOnPlanboard() {
      this.$setLocationPath(`planboard?booking=${this.bookingKey}`)
    },
    async sendEmailProxy(data) {
      const model = {
        BookingKey: this.bookingKey,
        MailType: data.mailType,
        AdditionalRecipients: data.cc ? [data.cc] : null,
      }
      await this.saveSomething({
        call: () => this.sendEmail(model),
        handleResponse: success => {
          if (success) {
            this.showEmailDialog = false
          }
        },
        successMessage: 'Email sent',
        errorMessage: 'Something went wrong',
      })
    },
    async addPayment(payment) {
      await this.saveSomething({
        call: () => this.savePayment(payment),
        handleResponse: success => {
          if (success) this.showAddPaymentDialog = false
        },
        successMessage: 'Payment saved',
        errorMessage: 'Something went wrong while saving payment',
      })
    },
    async addRefund(refundModel) {
      await this.saveSomething({
        call: () => this.savePayment(refundModel),
        handleResponse: success => {
          if (success) this.showRefundDialog = false
        },
        successMessage: 'Refund saved',
        errorMessage: 'Something went wrong while saving refund',
      })
    },
    async saveStatus(status) {
      await this.saveSomething({
        call: () => this.saveBookingStatus({ NewStatus: status }),
        successMessage: 'Booking status saved',
        errorMessage: 'Something went wrong while saving booking status',
      })
    },
    async saveMarkAsInvoice() {
      await this.saveSomething({
        call: this.markAsInvoice,
        handleResponse: success => {
          if (success) this.showInvoiceDialog = false
        },
        successMessage: 'Booking marked as pay by invoice',
        errorMessage: 'Something went wrong',
      })
    },
    async saveNewBookingStatus() {
      if (!this.selectedBookingStatus) {
        this.$notifyError('Please select a booking status')
        return
      }
      await this.saveStatus(this.selectedBookingStatus)
      this.selectedBookingStatus = null
      this.showBookingStatusDialog = false
    },
    async saveSomething({ call, successMessage, errorMessage, handleResponse }) {
      const success = await call()
      if (success) this.$notifySuccess(successMessage || 'Saved successfully')
      else this.$handleError({ error: this.saveError, notification: errorMessage })
      if (handleResponse) handleResponse(success)
    },
    emitShowQuickActionsValue(value) {
      this.$bus.$emit('showQuickActions', value)
    },
    closeQuickActions() {
      this.showQuickActions = false
    },
    peformIntegrationAction(integration, action) {
      if (action.ActionType === 'OnewayRedirect') {
        this.$emit('peformIntegrationActionRedirect', {
          identificationCode: integration.integrationCode,
          actionCode: action.ActionCode,
        })
      }
    },
  },
  watch: {
    showQuickActions(newVal) {
      this.emitShowQuickActionsValue(newVal)
    },
  },
  mounted() {
    this.$bus.$on('showRefundDialog', () => (this.showRefundDialog = true))
    this.$bus.$on('showAddPaymentDialog', () => (this.showAddPaymentDialog = true))
    this.$bus.$on('toggleQuickActions', () => {
      if (this.showQuickActions) this.showQuickActions = false
      else {
        this.quickActionsHaveFaded = false
        setTimeout(() => (this.showQuickActions = true), 200) // await .quick-actions transition (0.2s)
      }
    })
    this.emitShowQuickActionsValue(this.showQuickActions)
    this.$bus.$on('sendMailBtnClicked', () => (this.showEmailDialog = true))
  },
}
</script>

<style scoped lang="stylus">
.quick-actions
  height 100%
  background #121d32
  color white
  position relative
  width 264px
  transition width 0.2s ease

  .quick-actions-title
    display flex

    .actions-icon
      margin-top -2px
      margin-right 4px

  .grey
    color #798696

  &.collapsed
    width 0

  .quick-actions-content
    height 100%
    padding 36px 24px 16px 34px
    display flex
    flex-direction column

  .collapse-icon-button
    color white
    fill white
    position absolute
    right 8px
    top 8px

  .briq-button
    margin-bottom 7px
    text-align left

.marked-as-checked-reset-icon
  margin-left 20px
  margin-top -2px
</style>
